import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";

const KEY = "ss26.rate_limit.v1";
const WINDOW_MS = 2 * 60 * 1000; // 2 minutes
const MAX_ATTEMPTS = 10;
const CHALLENGE_BONUS = 5;
const BLOCK_MS = 5 * 60 * 1000;

type State = {
  endpoint: string;
  attempts: number;
  windowStart: number;
  challengeRequired: boolean;
  challengePassed: boolean;
  blockedUntil: number | null;
};

function load(endpoint: string): State {
  try {
    const raw = localStorage.getItem(`${KEY}:${endpoint}`);
    if (!raw) throw new Error();
    return JSON.parse(raw) as State;
  } catch {
    return { endpoint, attempts: 0, windowStart: Date.now(), challengeRequired: false, challengePassed: false, blockedUntil: null };
  }
}

function save(s: State) {
  try { localStorage.setItem(`${KEY}:${s.endpoint}`, JSON.stringify(s)); } catch { /* noop */ }
}

export type RateLimitDecision =
  | { ok: true }
  | { ok: false; reason: "challenge_required"; retryAfterMs?: number }
  | { ok: false; reason: "blocked"; retryAfterMs: number };

async function logAttempt(state: State, ip?: string) {
  try {
    await supabase.from("rate_limit_attempts").insert({
      ip_address: ip ?? "client-unknown",
      endpoint: state.endpoint,
      attempts: state.attempts,
      challenge_required: state.challengeRequired,
      challenge_passed: state.challengePassed,
      blocked_until: state.blockedUntil ? new Date(state.blockedUntil).toISOString() : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  } catch { /* best effort */ }
}

export function checkRateLimit(endpoint: string): RateLimitDecision {
  const s = load(endpoint);
  const now = Date.now();
  if (s.blockedUntil && now < s.blockedUntil) {
    return { ok: false, reason: "blocked", retryAfterMs: s.blockedUntil - now };
  }
  if (s.blockedUntil && now >= s.blockedUntil) {
    save({ ...s, attempts: 0, windowStart: now, challengeRequired: false, challengePassed: false, blockedUntil: null });
    return { ok: true };
  }
  if (now - s.windowStart > WINDOW_MS) {
    save({ ...s, attempts: 0, windowStart: now, challengeRequired: false, challengePassed: false });
    return { ok: true };
  }
  const cap = s.challengePassed ? MAX_ATTEMPTS + CHALLENGE_BONUS : MAX_ATTEMPTS;
  if (s.attempts >= cap && !s.challengeRequired) {
    save({ ...s, challengeRequired: true });
    void trackEvent("turnstile_challenge_shown", { endpoint, attempts: s.attempts });
    return { ok: false, reason: "challenge_required" };
  }
  if (s.challengeRequired && !s.challengePassed) {
    return { ok: false, reason: "challenge_required" };
  }
  return { ok: true };
}

export function recordAttempt(endpoint: string) {
  const s = load(endpoint);
  const now = Date.now();
  if (now - s.windowStart > WINDOW_MS && !s.blockedUntil) {
    s.attempts = 0; s.windowStart = now; s.challengeRequired = false; s.challengePassed = false;
  }
  s.attempts += 1;
  save(s);
  if (s.attempts === MAX_ATTEMPTS) void trackEvent("rate_limit_warning", { endpoint, attempts: s.attempts });
  void logAttempt(s);
}

export function passChallenge(endpoint: string) {
  const s = load(endpoint);
  s.challengePassed = true;
  s.challengeRequired = false;
  save(s);
  void trackEvent("turnstile_challenge_passed", { endpoint });
  void logAttempt(s);
}

export function failChallenge(endpoint: string) {
  const s = load(endpoint);
  s.challengePassed = false;
  s.blockedUntil = Date.now() + BLOCK_MS;
  save(s);
  void trackEvent("turnstile_challenge_failed", { endpoint });
  void trackEvent("ip_temporarily_blocked", { endpoint, ms: BLOCK_MS });
  void logAttempt(s);
}

export function rateLimitMessage(): string {
  return "Too many attempts. For security reasons, please try again in 5 minutes.";
}